import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps} from 'helpers/hooks/utils';
import StyledCollectionBasicsSectionPanelContent
  from 'components/organisms/SectionPanels/CollectionBasicsSectionPanelContent/CollectionBasicsSectionPanelContent.styles';
import CollectionProfile from 'components/organisms/Profiles/CollectionProfile/CollectionProfile';
import CollectionSectionPanelHeader
  from 'components/organisms/SectionPanels/CollectionSectionPanelHeader/CollectionSectionPanelHeader';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from 'components/atoms/Layout/Box/Box';
import Typography from 'components/atoms/Text/Typography/Typography';
import utils from 'helpers/utils';
import ScoreLinearProgress from 'components/organisms/Progress/ScoreLinearProgress/ScoreLinearProgress';
import {useCollectionStatCredits} from 'services/collection/stat/stat.utils';

const info = 'Manage the basic collection settings';

const CollectionBasicsSectionPanelContent = React.forwardRef((props, ref) => {
  const {
    section,
    collection,
    isLoading,
    onCanUpdate,
    ...innerProps
  } = useComponentProps(props, 'CollectionBasicsSectionPanelContent');

  const smDown = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const [creditsBreakdown, breakdownIsLoading] = useCollectionStatCredits(
    !isLoading ? collection?.collectionId : null, null, 1
  );

  const [creditsUsed, creditsAverage] = useMemo(() => {
    if (+creditsBreakdown?.total > 0) {
      return [
        Math.floor(creditsBreakdown.stats.reduce((t, c) => t + +c.credits, 0)),
        Math.floor(+creditsBreakdown.average)
      ];
    } else {
      return [0, 0];
    }
  }, [creditsBreakdown]);

  const handleShowCard = (card) => {
    return ['scope'].includes(card.name);
  }

  const renderFooter = () => {
    const scoreIsLoading = breakdownIsLoading || isLoading;
    const creditsScore = +creditsAverage > 0 ? ((creditsUsed / +creditsAverage) * 100) : 0;

    return <Box className="CollectionBasicsSectionPanelContent-footer">
      <Box className="CollectionBasicsSectionPanelContent-footer-header">
        <Typography variant="subtitle2">Credits used{!smDown ? ' this month' : ''}{!collection?.isActive ? ' (inactive)' : ''}</Typography>
        <Typography isLoading={scoreIsLoading} variant="subtitle2">
          {utils.formatNumber(creditsUsed)} of {utils.formatNumber(creditsAverage)}{!smDown ? ' average usage' : ''}
        </Typography>
      </Box>
      <ScoreLinearProgress showEmpty={true}
                           showPercentage={creditsScore >= 20}
                           score={creditsScore}
                           color={!collection?.isActive ? 'warning' : 'success'}
                           isLoading={scoreIsLoading}/>
    </Box>
  }

  return <StyledCollectionBasicsSectionPanelContent ref={ref} {...innerProps}
                                                    footer={renderFooter()}>
    <CollectionSectionPanelHeader title={section.title} info={!smDown ? info : null} />
    <CollectionProfile columns={1}
                       onCanUpdate={onCanUpdate}
                       onShowCard={handleShowCard} />
  </StyledCollectionBasicsSectionPanelContent>
});

CollectionBasicsSectionPanelContent.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  section: PropTypes.object,
  collection: PropTypes.object,
  isLoading: PropTypes.bool,
  onCanUpdate: PropTypes.func
};

export default CollectionBasicsSectionPanelContent;
